import { useMemo } from "react";
import { TIFFormDataProps, useTIFFormData } from "../../../../hooks";
import { EventsLeadingToDeathOptions, PastMedicalHistory, PastProceduresSurgicalHistory } from "../../../../models";
import { Col, Row } from "react-bootstrap";
import { get } from "lodash-es";
import { TextFit } from "../../../../component-library";

export function TIFSummaryRecords({ tissueId }: Readonly<TIFFormDataProps>) {
    const {
        pastMedicalHistoryKeys,
        pastMedicalHistory,
        summaryOfRecordsData,
        pastProceduresSurgicalHistoryKeys,
        pastProceduresSurgicalHistory,
        wbcExists,
        wbcValue,
        tempExists,
        tempValue,
        bloodCultureExists,
        urineCultureExists,
        sputumCultureExists,
        otherCultureExists,
        bloodCultureValue,
        urineCultureValue,
        sputumCultureValue,
        otherCultureValue,
    } = useTIFFormData({ tissueId });

    const eventLeadingToDonorDeathSentence = useMemo(() => {
        const selectedOption = EventsLeadingToDeathOptions.find(
            (option) => option.text === summaryOfRecordsData.eventsLeadingToDonorsDeath?.radio
        );
        const inputs = selectedOption?.inputs ?? [];
        const parts = selectedOption?.text.split(/(\{input\d+\})/g) ?? [];
        return parts.map((part, index) => {
            const input = inputs.find((input: any) => input.placeholder === part);
            if (input) {
                return <span key={index}>{get(summaryOfRecordsData, input.accessor)}</span>;
            } else {
                return part.includes("Other: ") ? part.replace("Other: ", "") : part;
            }
        });
    }, [summaryOfRecordsData]);

    return (
        <>
            <TextFit style={{ width: "100%", maxHeight: "180px" }} maxFontSizePx={14}>
                <div style={{ fontWeight: "bold" }}>Summary of Records:</div>
                {pastMedicalHistoryKeys.length ? (
                    <Row>
                        <Col>
                            <b>Past Medical History: </b>
                            {pastMedicalHistoryKeys.map((key, i) => {
                                const value = pastMedicalHistory[key];
                                const isLast = i === pastMedicalHistoryKeys.length - 1;
                                return (
                                    <span key={key}>
                                        {key === "other" ? (
                                            value.text
                                        ) : (
                                            <>
                                                {PastMedicalHistory[key as keyof typeof PastMedicalHistory]}
                                                {value.text ? `: ${value.text}` : ""}
                                            </>
                                        )}
                                        {!isLast && ", "}
                                    </span>
                                );
                            })}
                        </Col>
                    </Row>
                ) : (
                    ""
                )}
                {eventLeadingToDonorDeathSentence.length ? (
                    <Row>
                        <Col>
                            <b>Events Leading to Donor's Death: </b>
                            {eventLeadingToDonorDeathSentence}
                            {summaryOfRecordsData.eventsLeadingToDonorsDeath?.ventilator && (
                                <span>
                                    The donor on a ventilator, amount of time:{" "}
                                    {summaryOfRecordsData.eventsLeadingToDonorsDeath.ventilatorTime}
                                </span>
                            )}
                        </Col>
                    </Row>
                ) : (
                    ""
                )}
                {pastProceduresSurgicalHistoryKeys.length ? (
                    <Row>
                        <Col>
                            <b>Past Procedures/Surgical History: </b>
                            {pastProceduresSurgicalHistoryKeys.map((key, i) => {
                                const value = pastProceduresSurgicalHistory[key];
                                const isLast = i === pastProceduresSurgicalHistoryKeys.length - 1;
                                return (
                                    <span key={key}>
                                        {key === "other" ? (
                                            value.text
                                        ) : (
                                            <>
                                                {
                                                    PastProceduresSurgicalHistory[
                                                        key as keyof typeof PastProceduresSurgicalHistory
                                                    ]
                                                }
                                                {value.text ? `: ${value.text}` : ""}
                                            </>
                                        )}
                                        {!isLast && ", "}
                                    </span>
                                );
                            })}
                        </Col>
                    </Row>
                ) : (
                    ""
                )}
                <Row>
                    <Col>
                        {!summaryOfRecordsData.laboratoryResults?.WhiteBloodCellCountNA && wbcExists !== undefined && (
                            <>
                                <b>WBC:</b>{" "}
                                {summaryOfRecordsData.laboratoryResults?.WhiteBloodCellCountNA ? "WBC: N/A" : wbcValue}
                            </>
                        )}
                        {!summaryOfRecordsData.laboratoryResults?.TemperaturesNA && tempExists !== undefined && (
                            <>
                                <b> Body Temp:</b>{" "}
                                {summaryOfRecordsData.laboratoryResults?.TemperaturesNA ? "Body Temp: N/A" : tempValue}
                            </>
                        )}
                        {(summaryOfRecordsData.laboratoryResults?.BloodNA || bloodCultureExists) && (
                            <>
                                <b> Blood Culture:</b>{" "}
                                {summaryOfRecordsData.laboratoryResults?.BloodNA ? "N/A" : bloodCultureValue}
                            </>
                        )}
                        {(summaryOfRecordsData.laboratoryResults?.UrineNA || urineCultureExists) && (
                            <>
                                <b> Urine Culture:</b>{" "}
                                {summaryOfRecordsData.laboratoryResults?.UrineNA ? "N/A" : urineCultureValue}
                            </>
                        )}
                        {(summaryOfRecordsData.laboratoryResults?.SputumNA || sputumCultureExists) && (
                            <>
                                <b> Sputum Culture:</b>{" "}
                                {summaryOfRecordsData.laboratoryResults?.SputumNA ? "N/A" : sputumCultureValue}
                            </>
                        )}
                        {(summaryOfRecordsData.laboratoryResults?.OtherNA || otherCultureExists) && (
                            <>
                                <b> {summaryOfRecordsData.laboratoryResults?.OtherText}:</b>{" "}
                                {summaryOfRecordsData.laboratoryResults?.OtherNA
                                    ? `${summaryOfRecordsData.laboratoryResults?.OtherText}: N/A`
                                    : otherCultureValue}
                            </>
                        )}
                    </Col>
                </Row>
            </TextFit>
            <hr className="my-1" />
        </>
    );
}
